import { hashInts } from "../shared/hash";
import { sub } from "../shared/fixed";
import type { MatchRules } from "./ability";

/** Состояния бойца — явный автомат (GAME-04). Переходы — в fighter.ts. */
export const FIGHTER = {
  idle: 0,
  walkForward: 1,
  walkBack: 2,
  crouch: 3,
  jump: 4,
  ability: 5,
  block: 6,
  crouchBlock: 7,
  hitstun: 8,
  blockstun: 9,
  airHit: 10,
  knockdown: 11,
  getup: 12,
  ko: 13,
  victory: 14,
  /** Суперприём: открывающий удар и серия в заставке. */
  superAttack: 15,
  /** Под серией суперприёма. */
  superVictim: 16,
  /** Разворот к сопернику: короткое движение, а не мгновенный флип (Джон 04.11.2026). */
  turn: 17,
  /** Его бросают: идёт по пути пары клипов броска (sim/throw.ts), бить нельзя, вырваться — L1 в первые кадры. */
  thrown: 18,
} as const;
export type FighterStateId = (typeof FIGHTER)[keyof typeof FIGHTER];

export const PHASE = { intro: 0, fight: 1, ko: 2, timeUp: 3, matchEnd: 4 } as const;

export const HISTORY = 16;
export const BUTTON_COUNT = 4;
export const PRESS_NEVER = 99;
/** Сколько последних касаний направлений помним (самый длинный жест — ← ← ←). */
export const TAPS = 3;

/** Снимок бойца: только целые числа, чтобы снимок копировался и сравнивался (откат кадров, GAME-27). */
export interface FighterState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: number;
  state: FighterStateId;
  stateFrame: number;
  /** Индекс способности в наборе героя или -1. */
  ability: number;
  abilityFrame: number;
  hasHit: number;
  stun: number;
  health: number;
  meter: number;
  juggle: number;
  invisible: number;
  /** Сколько кадров ещё стоит спиной, не разворачиваясь: соперник скачком ушёл за спину. */
  turnLock: number;
  armor: number;
  wins: number;
  lastBits: number;
  /** Сколько кадров назад зажаты L2 + R2 вместе (суперприём). */
  superAge: number;
  /** Сколько кадров назад нажаты курки суперприёма (L2, R2) и кнопка издёвки: «вместе» — в окне SUPER_WINDOW. */
  triggerAge: number[];
  /** Сколько кадров подряд игрок ничего не жмёт: простой — авто-издёвка (консультант 04.11.2026). */
  quiet: number;
  /** Как падает сбитый: 0 — обычно, 1 — споткнулся от подсечки (Soccer Trip). */
  fallKind: number;
  /** Где бросающий стоял в начале броска: начало пути пары клипов (подпиксели). */
  throwX: number;
  /** Последнее попадание по бойцу: куда (1 — голова, 2 — корпус, 3 — ноги) и сила (0–2) — для реакции тела. */
  hurtSpot: number;
  hurtPower: number;
  /**
   * Серое здоровье (Джон 05.11.2026, 00:06: «интриги, как в Последнем тейкене»): часть снятого, которую можно отыграть
   * своей атакой; пропущенный удар её сжигает (combat.ts).
   */
  grey: number;
  /** Бег: двойное «вперёд», держится, пока держат «вперёд». */
  running: number;
  /** Сколько ударов в текущей цепочке отмен (до CHAIN_LIMIT); с нуля — у удара из стойки. */
  chain: number;
  /**
   * Окно серии после удара: какой удар закончился (индекс в наборе) и сколько кадров ещё ждём следующего нажатия —
   * серия идёт и у того, кто жмёт не чаще 4 раз в секунду (Джон 04.11.2026, 23:13: «он просто делает один и тот же удар»).
   */
  comboAbility: number;
  comboLeft: number;
  /** Шаг продолжения серии: скорость вдогонку, подпиксели за кадр, держится до первого активного кадра удара. */
  chase: number;
  /** Направления от лица героя за последние кадры, кольцом. */
  history: number[];
  historyHead: number;
  /** Сколько кадров назад нажата каждая кнопка удара. */
  pressAge: number[];
  /** Удержание кнопок удара: > 0 — держат столько кадров; < 0 — отпустили в этот кадр, продержав −n; 0 — не нажата. */
  held: number[];
  /**
   * Касания направлений для жестов (← →, ← ← ←, ↓ ↑), новое — первым: код (0 — нет, TAP_CODE), сколько кадров назад
   * началось, сколько держали (> 0 — ещё держат, < 0 — отпустили).
   */
  tapCode: number[];
  tapAge: number[];
  tapHeld: number[];
}

/** Снаряд бойца в полёте: один на бойца (у каждого свой слот). Целые подпиксели, как у бойца. */
export interface ShotState {
  /** 0 — нет снаряда, 1 — летит. */
  live: number;
  x: number;
  y: number;
  vx: number;
  /** Чья способность (индекс в наборе владельца) и сколько кадров летит. */
  ability: number;
  age: number;
}

export const emptyShot = (): ShotState => ({ live: 0, x: 0, y: 0, vx: 0, ability: -1, age: 0 });

export interface MatchState {
  frame: number;
  phase: number;
  phaseFrame: number;
  round: number;
  timer: number;
  hitstop: number;
  rng: number;
  winner: number;
  /** Чья заставка суперприёма идёт (0, 1) или -1; кадр заставки. */
  cinematic: number;
  cinematicFrame: number;
  /**
   * Решающий удар (sim/decisive.ts): сколько обработанных кадров мира ещё в замедлении, какой кадр из SLOW_RATE сейчас,
   * накопленные нажатия обоих, было ли замедление в этом раунде и чей удар.
   */
  slowmo: number;
  slowTick: number;
  slowBits: number[];
  slowmoDone: number;
  slowmoBy: number;
  fighters: [FighterState, FighterState];
  shots: [ShotState, ShotState];
}

export function createFighter(rules: MatchRules, side: 0 | 1): FighterState {
  const hero = rules.heroes[side];
  const center = (rules.arena.left + rules.arena.right) / 2;
  const offset = side === 0 ? -rules.arena.startGap / 2 : rules.arena.startGap / 2;
  return {
    x: sub(center + offset),
    y: 0,
    vx: 0,
    vy: 0,
    facing: side === 0 ? 1 : -1,
    state: FIGHTER.idle,
    stateFrame: 0,
    ability: -1,
    abilityFrame: 0,
    hasHit: 0,
    stun: 0,
    health: hero.health,
    meter: 0,
    juggle: 0,
    invisible: 0,
    turnLock: 0,
    armor: 0,
    wins: 0,
    lastBits: 0,
    superAge: PRESS_NEVER,
    triggerAge: [PRESS_NEVER, PRESS_NEVER, PRESS_NEVER, PRESS_NEVER],
    quiet: 0,
    fallKind: 0,
    throwX: 0,
    hurtSpot: 0,
    hurtPower: 0,
    grey: 0,
    running: 0,
    chain: 0,
    comboAbility: -1,
    comboLeft: 0,
    chase: 0,
    history: new Array<number>(HISTORY).fill(0),
    historyHead: 0,
    pressAge: new Array<number>(BUTTON_COUNT).fill(PRESS_NEVER),
    held: new Array<number>(BUTTON_COUNT).fill(0),
    tapCode: new Array<number>(TAPS).fill(0),
    tapAge: new Array<number>(TAPS).fill(PRESS_NEVER),
    tapHeld: new Array<number>(TAPS).fill(0),
  };
}

export function createMatch(rules: MatchRules, seed: number): MatchState {
  return {
    frame: 0,
    phase: PHASE.intro,
    phaseFrame: 0,
    round: 1,
    timer: rules.roundSeconds * 60,
    hitstop: 0,
    rng: seed | 0,
    winner: -1,
    cinematic: -1,
    cinematicFrame: 0,
    slowmo: 0,
    slowTick: 0,
    slowBits: [0, 0],
    slowmoDone: 0,
    slowmoBy: -1,
    fighters: [createFighter(rules, 0), createFighter(rules, 1)],
    shots: [emptyShot(), emptyShot()],
  };
}

const cloneFighter = (fighter: FighterState): FighterState => ({
  ...fighter,
  history: fighter.history.slice(),
  pressAge: fighter.pressAge.slice(),
  held: fighter.held.slice(),
  triggerAge: fighter.triggerAge.slice(),
  tapCode: fighter.tapCode.slice(),
  tapAge: fighter.tapAge.slice(),
  tapHeld: fighter.tapHeld.slice(),
});

/** Копия снимка для отката кадров. */
export function cloneMatch(state: MatchState): MatchState {
  return {
    ...state,
    fighters: [cloneFighter(state.fighters[0]), cloneFighter(state.fighters[1])],
    shots: [{ ...state.shots[0] }, { ...state.shots[1] }],
    slowBits: state.slowBits.slice(),
  };
}

function fighterInts(fighter: FighterState): number[] {
  return [
    fighter.x,
    fighter.y,
    fighter.vx,
    fighter.vy,
    fighter.facing,
    fighter.turnLock,
    fighter.state,
    fighter.stateFrame,
    fighter.ability,
    fighter.abilityFrame,
    fighter.hasHit,
    fighter.stun,
    fighter.health,
    fighter.meter,
    fighter.juggle,
    fighter.invisible,
    fighter.armor,
    fighter.wins,
    fighter.lastBits,
    fighter.historyHead,
    fighter.chain,
    fighter.quiet,
    fighter.fallKind,
    fighter.throwX,
    fighter.hurtSpot,
    fighter.hurtPower,
    fighter.grey,
    fighter.comboAbility,
    fighter.comboLeft,
    fighter.chase,
    ...fighter.history,
    ...fighter.pressAge,
    ...fighter.triggerAge,
    ...fighter.tapCode,
    ...fighter.tapAge,
    ...fighter.tapHeld,
  ];
}

/** Сумма снимка: сверка сторон онлайн и проверка повторов. */
export function hashMatch(state: MatchState): number {
  return hashInts([
    state.frame,
    state.phase,
    state.phaseFrame,
    state.round,
    state.timer,
    state.hitstop,
    state.rng,
    state.winner,
    state.slowmo,
    state.slowTick,
    ...state.slowBits,
    state.slowmoDone,
    state.slowmoBy,
    ...fighterInts(state.fighters[0]),
    ...fighterInts(state.fighters[1]),
    ...state.shots.flatMap((shot) => [shot.live, shot.x, shot.y, shot.vx, shot.ability, shot.age]),
  ]);
}
